/**
 * ONE-TAP SUGGESTIONS — the values a developer will not fill in for themselves.
 *
 * A card raised in a hurry carries a title and little else: no size, no due date.
 * Those blanks are not laziness, they are friction — the person does not know the
 * number off the top of their head and will not stop to work it out. So the board
 * works it out FOR them from the cards that look like this one, and offers it as a
 * value they can accept in a single tap.
 *
 * THE RULE THAT MATTERS MOST IS WHEN NOT TO SUGGEST. A confident, well-grounded
 * suggestion earns a tap; a bad guess earns distrust, and after one bad guess the
 * person reads every suggestion as noise. So both suggesters are CONFIDENCE-GATED:
 * each returns `null` whenever it cannot stand behind the value, and the surface
 * shows nothing at all rather than a shrug. A blank is a better answer than a wrong
 * one.
 *
 * PURE ON PURPOSE, exactly like src/idle-nudge.ts, src/dependencies.ts and
 * src/progress-health.ts — no store, no React, no clock of its own. The caller
 * passes the card, the pool of cards to learn from, and (for the due date) the
 * card's phase and sprint windows. That is what lets the rules be proved against
 * fixed fixtures in scripts/test-suggest.ts rather than argued about. The date
 * maths is reused from src/sprints.ts (`addDays`, `daysBetween`) rather than
 * reinvented, for the daylight-saving reason those helpers spell out.
 */
import type { Issue, IssueSize } from './types';
import { SIZE_ORDER } from './types';
import type { Phase } from './phases';
import type { Sprint } from './sprints';
import { addDays, daysBetween, today } from './sprints';
import { personalDueForecast, cycleTimeFinishBy, type HistoryCard } from './forecast';
import type { WorkCalendar } from './work-calendar';

/**
 * THE SLICE OF A CARD THE SUGGESTERS READ — a Pick of Issue, so a real Issue passes
 * straight in and a test fixture only has to carry these fields (the idle-nudge /
 * dependencies idiom). `id` is here to exclude the card itself from its own pool of
 * comparables; `kind`/`epic`/`phase` are the comparability keys; `size` and the two
 * dates are what a comparable contributes; `status` is read only to skip a resolved
 * card, which never wants a suggestion.
 */
export type SuggestCard = Pick<
  Issue,
  'id' | 'kind' | 'epic' | 'phase' | 'size' | 'status' | 'startsOn' | 'dueDate'
>;

/** A size suggestion: the size to offer, and a short human reason to show beside it. */
export interface SizeSuggestion {
  size: IssueSize;
  basis: string;
}

/** A due-date suggestion: the day (`YYYY-MM-DD`) to offer, and the reason for it.
 *  `low`/`high` are an optional honest range (p25/p75) that only the personal
 *  forecast (source 0) carries — a surface with room shows "~18 Nov (14–22 Nov)",
 *  a tight one shows just `due`; the board-shaped sources leave them undefined. */
export interface DueSuggestion {
  due: string;
  basis: string;
  low?: string;
  high?: string;
}

/**
 * WHAT THE PERSONAL FORECAST (source 0) NEEDS: the owner to model, a label for the
 * basis sentence (a display name, falling back to the owner id), the pool of finished
 * cards to learn their pace from (board-local today, cross-board as the portfolio read
 * widens), and the working calendar. Absent = no personal read, the cascade starts at
 * the phase end as before. Kept as one optional bag so the existing callers are
 * untouched and a caller opts in by passing it.
 */
export interface PersonalContext {
  owner: string;
  ownerLabel?: string;
  history: HistoryCard[];
  cal?: WorkCalendar;
}

/**
 * HOW MANY COMPARABLE CARDS A MEDIAN NEEDS BEFORE IT IS TRUSTED. Below this the
 * sample is too small to stand behind — one or two cards "like this one" is an
 * anecdote, not a basis — and the suggester returns null rather than guess from it.
 */
const MIN_COMPARABLES = 3;

/** Which grouping two cards must share to count as comparable, beside their type. */
type Comparator = 'epic' | 'phase';

/** The word a comparator reads as in a basis string ("…in this epic/phase…"). */
const COMPARATOR_WORD: Record<Comparator, string> = { epic: 'epic', phase: 'phase' };

/**
 * THE SIZE WORD a basis reads as — capitalised and plain ("Medium"), NOT the UI
 * label `SIZE_LABEL` ("A few days"), which does not sit inside a sentence ("…are A
 * few days"). A reason is prose, so it names the size the way a person says it.
 */
export const SIZE_WORD: Record<IssueSize, string> = { quick: 'Quick', medium: 'Medium', long: 'Long' };

/** Resolved work — done or dropped — is finished and never wants a suggestion. */
function isResolved(card: SuggestCard): boolean {
  return card.status === 'done' || card.status === 'wontdo';
}

/** Whether a date field actually names a day — the src/dependencies.ts rule: null
 *  clears the column, absent means unspecified, '' is a blank; all read as "no day". */
function hasDay(d: string | null | undefined): d is string {
  return typeof d === 'string' && d.length > 0;
}

/**
 * THE VALUE OF A CARD'S COMPARATOR KEY, or '' when it carries none. A card with no
 * epic (or no phase) has an EMPTY key, and an empty key never matches — "cards with
 * no epic" is not a meaningful peer group to size or schedule against, so a card
 * missing the key simply cannot be compared by it and falls through to the next
 * comparator (or to null). This is why `groupKey` returns '' rather than matching
 * absence to absence.
 */
function groupKey(card: SuggestCard, by: Comparator): string {
  const v = by === 'epic' ? card.epic : card.phase;
  return v ?? '';
}

/**
 * THE COMPARABLE SET AND HOW IT WAS FOUND — same TYPE and same EPIC first, falling
 * back to same TYPE and same PHASE, and null when neither yields enough.
 *
 * `has` is what makes a card a USABLE comparable for the question being asked: a
 * size median needs cards that carry a size; a duration median needs cards that
 * carry both dates. A card that matches the type and grouping but lacks the field
 * is not counted, because it contributes nothing to the median — so the >= 3 floor
 * is three cards that can actually be averaged, not three that merely sit in the
 * same epic. The card itself is always excluded (`id`), so it never props up its own
 * sample.
 *
 * EPIC IS PREFERRED over phase because it is the tighter likeness: cards in one epic
 * are one body of work, where a phase ("Build") spans many. The fallback widens the
 * net only when the tighter net came up short.
 */
function pickComparables(
  card: SuggestCard,
  pool: SuggestCard[],
  has: (c: SuggestCard) => boolean,
): { set: SuggestCard[]; by: Comparator } | null {
  for (const by of ['epic', 'phase'] as Comparator[]) {
    const key = groupKey(card, by);
    if (!key) continue; // the card is not in this grouping — it cannot compare by it
    const set = pool.filter(
      (c) => c.id !== card.id && c.kind === card.kind && groupKey(c, by) === key && has(c),
    );
    if (set.length >= MIN_COMPARABLES) return { set, by };
  }
  return null;
}

/**
 * THE INDEX OF THE MEDIAN of a sorted-ascending array.
 *
 * TIE RULE, STATED ONCE AND USED FOR BOTH MEDIANS: an even count takes the
 * LOWER-MIDDLE element — `arr[n/2 - 1]` — never the higher one and never an average
 * of the two. Averaging is meaningless for an ordered size scale (there is no size
 * "between" Quick and Medium), and choosing one rule for both the size and the
 * duration medians keeps them from disagreeing at an even boundary. Lower-middle is
 * the more conservative half: the smaller size, the shorter span.
 */
function medianIndex(length: number): number {
  return length % 2 ? (length - 1) / 2 : length / 2 - 1;
}

/**
 * SUGGEST A SIZE for a card that has none, from the median size of comparable cards.
 *
 * Returns null — meaning "show nothing" — when:
 *   · the card already carries a size (nothing to suggest), or
 *   · the card is resolved (finished work is not sized after the fact), or
 *   · there are fewer than MIN_COMPARABLES sized comparables in either grouping.
 *
 * Otherwise the suggested size is the true median over the ordered size scale
 * (SIZE_ORDER: quick < medium < long) of the comparables' sizes, and `basis` names
 * the sample it came from — "6 similar cards in this epic are Medium" — with the
 * count, the comparator (epic vs phase) and the size all read correctly.
 */
export function suggestSize(card: SuggestCard, pool: SuggestCard[]): SizeSuggestion | null {
  if (card.size) return null; // '' and undefined both mean unset; a real size is left alone
  if (isResolved(card)) return null;

  const found = pickComparables(card, pool, (c) => !!c.size);
  if (!found) return null;

  const { set, by } = found;
  const indices = set.map((c) => SIZE_ORDER.indexOf(c.size as IssueSize)).sort((a, b) => a - b);
  const size = SIZE_ORDER[indices[medianIndex(indices.length)]];

  const n = set.length;
  const basis = `${n} similar card${n === 1 ? '' : 's'} in this ${COMPARATOR_WORD[by]} ${
    n === 1 ? 'is' : 'are'
  } ${SIZE_WORD[size]}`;
  return { size, basis };
}

/**
 * SUGGEST A DUE DATE for a card that has none, from the most confident source there
 * is, in this order:
 *
 *   0. HOW LONG THIS OWNER'S CARDS OF THIS SIZE ACTUALLY TAKE (Layer B, piece D). When
 *      a `personal` context is supplied and the card's owner has enough finished cards
 *      of this size to stand behind (src/forecast.ts), the date is today + their own
 *      median working-day span — the pace the date is really a promise about. It leads
 *      because it is read off the PERSON, not the board average; it withholds (and the
 *      phase/sprint/median sources answer) whenever the sample is too thin. Basis:
 *      "about how long Marcia's Medium cards take, 14 of them".
 *   1. THE CARD'S PHASE END. A card inside a phase that has a `targetEnd` is due, at
 *      the latest, when its phase is — the strongest signal there is, and it needs
 *      no sample. Basis: "end of the Build phase".
 *   2. THE CARD'S SPRINT END. Failing a phase window, a card committed to a sprint is
 *      due by the end of that sprint. Basis: "end of Sprint 7".
 *   3. START PLUS THE MEDIAN DURATION of comparable cards. Failing both windows, if
 *      the card has a start day and there are >= MIN_COMPARABLES comparables carrying
 *      BOTH a start and a due, the suggestion is the card's start plus the median of
 *      those comparables' durations. Basis: "start + 5 days, the median of similar
 *      cards".
 *
 * Returns null — "show nothing" — when the card already has a due date, when it is
 * resolved, or when none of the three sources yields a confident value.
 *
 * NEVER IN THE PAST (Vinnie, 25 Oct 2026). Each source draws on a date that may
 * already have passed — a phase or sprint that ended before today, or a start old
 * enough that start + the usual duration lands behind us. A due date in the past is
 * worse than none: it reads as overdue the instant it is written. So every source
 * stands only when its date is today or later; a stale one is skipped and the next,
 * more-recent source is tried, and when all three are spent the suggester offers
 * nothing rather than a day already gone. `now` is injectable for tests.
 *
 * DURATION IS MEASURED WITH `daysBetween`, which is end-inclusive (a one-day card
 * spans 1 day), and the suggestion is the card's start PLUS that many days — the
 * literal "start + N days" the basis reads. (This is a deliberately plain reading of
 * "start plus the median duration"; it does not subtract a day the way the cascade's
 * span-preservation in src/dependencies.ts does, because here the basis is meant to
 * say, and the dates are meant to show, "N days after it starts".)
 */
export function suggestDue(
  card: SuggestCard,
  phase: Pick<Phase, 'name' | 'targetEnd'> | null | undefined,
  pool: SuggestCard[],
  sprint?: Pick<Sprint, 'name' | 'endsOn'> | null,
  now: number = Date.now(),
  personal?: PersonalContext | null,
): DueSuggestion | null {
  if (hasDay(card.dueDate)) return null; // a due date already set is left alone
  if (isResolved(card)) return null;

  // The floor every suggestion must clear: today, in the reader's own timezone.
  // Same ISO shape as the dates it is compared with, so a plain string compare holds.
  const floor = today(now);

  // The data-grounded reads LEAD the plan boundaries (Vinnie, 31 Oct — the personal
  // forecast is source 0). Both land on a future working day already, so they only need
  // the same not-in-the-past floor.
  if (personal && card.size) {
    // 0 — how long THIS OWNER'S cards of THIS SIZE actually take. The most specific read,
    // so it leads; the honest p25/p75 range rides along for a surface with room (a stale
    // end behind today is dropped so the range never shows a day already gone).
    if (personal.owner) {
      const pf = personalDueForecast(personal.owner, card.size, personal.history, now, personal.cal);
      if (pf && pf.due >= floor) {
        const who = personal.ownerLabel && personal.ownerLabel.length > 0 ? personal.ownerLabel : personal.owner;
        return {
          due: pf.due,
          basis: `about how long ${who}'s ${SIZE_WORD[card.size]} cards take, ${pf.sample} of them`,
          low: pf.low >= floor ? pf.low : undefined,
          high: pf.high >= floor ? pf.high : undefined,
        };
      }
    }
    // 0.5 — how long COMPARABLE cards actually take, at a confidence (piece C). Not tied
    // to one owner, so it answers when the doer has no history of their own but the board
    // does. The percentile IS the confidence, so it carries no separate range.
    const cf = cycleTimeFinishBy(card.kind, card.size, personal.history, now, personal.cal);
    if (cf && cf.due >= floor) {
      return {
        due: cf.due,
        basis: `cards like this finish within ${cf.days} working day${cf.days === 1 ? '' : 's'} ${cf.pct}% of the time, ${cf.sample} of them`,
      };
    }
  }

  // 1 — the phase window end, when the card is in a phase whose end is still ahead.
  if (phase && hasDay(phase.targetEnd) && phase.targetEnd >= floor) {
    return { due: phase.targetEnd, basis: `end of the ${phase.name} phase` };
  }

  // 2 — the sprint window end, when that is the model the card runs in and it is yet to close.
  if (sprint && hasDay(sprint.endsOn) && sprint.endsOn >= floor) {
    return { due: sprint.endsOn, basis: `end of ${sprint.name}` };
  }

  // 3 — the card's own start plus the median duration of comparable cards.
  if (hasDay(card.startsOn)) {
    const found = pickComparables(card, pool, (c) => hasDay(c.startsOn) && hasDay(c.dueDate));
    if (found) {
      const durations = found.set
        .map((c) => daysBetween(c.startsOn as string, c.dueDate as string))
        .sort((a, b) => a - b);
      const days = durations[medianIndex(durations.length)];
      const due = addDays(card.startsOn, days);
      if (due >= floor) {
        return {
          due,
          basis: `start + ${days} ${days === 1 ? 'day' : 'days'}, the median of similar cards`,
        };
      }
    }
  }

  return null;
}
